import { makeStyles, InputBase } from '@material-ui/core'
import { unwrapResult } from '@reduxjs/toolkit'
import clsx from 'clsx'
import { Formik, useField } from 'formik'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import * as Yup from 'yup'

import { useAuth } from 'src/modules/auth'
import { useActionRunner } from 'src/modules/notification'
import {
  Button,
  Container,
  Form,
  PageTitle,
  Typography,
  FIELD_REQUIRED,
} from 'src/modules/ui'
import { formatDateTime } from 'src/utils'

import { useMessages } from '../hooks'

const useStyles = makeStyles(theme => ({
  message: {
    marginBottom: theme.spacing(4),
  },
  messageHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    marginBottom: theme.spacing(1),
  },
  messageHeaderNameContainer: {
    display: 'flex',
  },
  messageText: {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    whiteSpace: 'pre-line',
  },
  messageTextBuyer: {
    backgroundColor: theme.palette.fadedPurple.main,
  },
  messageTextSupplier: {
    backgroundColor: theme.palette.fadedGreen.main,
  },
  orgName: {
    marginBottom: 0,
  },
  replyInput: {
    backgroundColor: 'white',
    border: `1px solid ${theme.palette.fadedPurple.main}`,
    '&.Mui-focused': {
      borderColor: theme.palette.lightPurple.main,
    },
    '&:hover': {
      borderColor: theme.palette.lightPurple.main,
    },
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  supplierLogo: {
    borderRadius: theme.shape.borderRadius / 2,
    height: 35,
    marginRight: theme.spacing(1),
    objectFit: 'cover',
    width: 35,
  },
}))

const parseEnvironment = environment => {
  const { address } = environment
  const fields = ['name', 'line1', 'line2', 'postcode']
  return fields
    .filter(f => address[f])
    .map(f => address[f])
    .join(', ')
}

export const SingleConversation = ({
  buyer,
  environment,
  id,
  messages,
  subject,
  supplier,
}) => {
  const classes = useStyles()
  const { sendReply } = useMessages()
  const [doSendReply] = useActionRunner(sendReply)

  const subtitle = environment
    ? `For environment: ${parseEnvironment(environment)}`
    : null

  const validationSchema = Yup.object().shape({
    text: Yup.string().required(FIELD_REQUIRED),
  })
  const initialValues = { text: '' }
  const handleReply = async ({ text }, { resetForm }) => {
    const result = await doSendReply({ id, text })
    try {
      unwrapResult(result)
      resetForm()
    } catch (err) {}
  }

  return (
    <Container>
      <PageTitle subtitle={subtitle}>{subject}</PageTitle>
      {messages.map(({ createdAt, id, sender, text }) => (
        <div className={classes.message} key={id}>
          <div className={classes.messageHeader}>
            <div className={classes.messageHeaderNameContainer}>
              {sender.organisationType === 'supplier' && (
                <img
                  src={supplier.logo}
                  alt={supplier.name}
                  className={classes.supplierLogo}
                />
              )}
              <div>
                <Typography
                  variant="h4"
                  color="orange"
                  className={classes.orgName}
                >
                  {sender.organisationType === 'buyer'
                    ? buyer.name
                    : supplier.name}
                </Typography>
                <Typography variant="body2">
                  {sender.firstName} {sender.lastName} ({sender.jobTitle})
                </Typography>
              </div>
            </div>
            <Typography>{formatDateTime(createdAt)}</Typography>
          </div>
          <div
            className={clsx(classes.messageText, {
              [classes.messageTextBuyer]: sender.organisationType === 'buyer',
              [classes.messageTextSupplier]:
                sender.organisationType === 'supplier',
            })}
          >
            <Typography>{text}</Typography>
          </div>
        </div>
      ))}
      <Formik
        initialValues={initialValues}
        onSubmit={handleReply}
        validationSchema={validationSchema}
      >
        {({ handleSubmit, isSubmitting }) => (
          <Form className={classes.replyBox}>
            <ReplyInput name="text" />
            <Button
              color="primary"
              isLoading={isSubmitting}
              onClick={handleSubmit}
              size="large"
            >
              Reply
            </Button>
          </Form>
        )}
      </Formik>
    </Container>
  )
}

const ReplyInput = ({ name }) => {
  const classes = useStyles()
  const [field, meta] = useField(name)

  return (
    <InputBase
      {...field}
      className={classes.replyInput}
      error={meta.touched && meta.error ? true : false}
      fullWidth
      multiline
      name={name}
      placeholder="Type a reply..."
    />
  )
}

SingleConversation.propTypes = {
  id: PropTypes.number,
  buyer: PropTypes.shape({
    name: PropTypes.string,
  }),
  environment: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    address: PropTypes.shape({
      name: PropTypes.string,
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      county: PropTypes.string,
      postcode: PropTypes.string,
    }),
  }),
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      createdAt: PropTypes.string,
      sender: PropTypes.shape({
        id: PropTypes.string,
        email: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        jobTitle: PropTypes.string,
        organisationType: PropTypes.oneOf(['buyer', 'supplier']),
      }),
      text: PropTypes.string,
    })
  ),
  subject: PropTypes.string,
  supplier: PropTypes.shape({
    name: PropTypes.string,
    logo: PropTypes.string,
  }),
}

const SingleConversationContainer = ({ match }) => {
  const { id } = match.params

  const { userType } = useAuth()
  const {
    conversation,
    fetchSingleConversation,
    fetchUnreadConversationsCount,
    markConversationRead,
  } = useMessages()
  const [doFetchSingleConversation, isFetching] = useActionRunner(
    fetchSingleConversation,
    {
      initialFetchingState: true,
    }
  )

  useEffect(() => {
    doFetchSingleConversation(id)
  }, [])

  const markRead = async unreadMessageIds => {
    await markConversationRead({
      id,
      messageIds: unreadMessageIds,
    })
    await fetchUnreadConversationsCount()
  }

  const otherParty = {
    supplier: 'buyer',
    buyer: 'supplier',
  }

  useEffect(() => {
    if (!isFetching && conversation.id === Number(id)) {
      const unreadMessageIds = conversation.messages
        .filter(
          m => m.sender.organisationType === otherParty[userType] && !m.read
        )
        .map(m => m.id)

      if (unreadMessageIds.length) {
        markRead(unreadMessageIds)
      }
    }
  }, [isFetching, conversation])

  if (isFetching) {
    return null
  }

  return <SingleConversation {...conversation} />
}

export default SingleConversationContainer
